import {
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import { Chip } from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { impactDisplayMap } from "../../utils/requestBodies.js";
import DataTable from "./DataTable";
import { useExistingTableData } from "./useExistingTableData";

const RfiTable = ({ projectId, companyId, refreshTrigger, title }) => {
  const [data] = useExistingTableData(
    `${process.env.REACT_APP_API_BASE_URL}/change-management/companies/${companyId}/projects/${projectId}/rfis/`,
    projectId,
    companyId,
    refreshTrigger,
    "Failed to fetch RFIs",
    { suppressErrorToast: false }
  );

  const flattened = useMemo(() => {
    const mapped = (data || []).map((rfi) => {
      const flattenedItem = {
        id: rfi.id,
        subject: rfi.subject || "N/A",
        number: rfi.full_number || "N/A",
        due_date: rfi.due_date ? rfi.due_date : "N/A",
        rfi_manager_name: rfi.rfi_manager?.name || "N/A",
        assignee_name: (rfi.assignees || []).map((a) => a.name).join(", ") || "N/A",
        required_assignees:
          (rfi.assignees || [])
            .filter((a) => a.response_required)
            .map((a) => a.name)
            .join(", ") || "N/A",
        received_from_name: rfi.received_from?.name || "N/A",
        distribution_list: (rfi.distribution_list || []).map((d) => d.name).join(", ") || "N/A",
        responsible_contractor_name: rfi.responsible_contractor?.name || "N/A",
        specification:
          (typeof rfi.specification === "object" ? rfi.specification?.name : rfi.specification) ||
          rfi.specification_section?.name ||
          "N/A",
        location: (typeof rfi.location === "object" ? rfi.location?.name : rfi.location) || "N/A",
        rfi_stage:
          typeof rfi.project_stage === "object"
            ? rfi.project_stage?.name || "N/A"
            : rfi.project_stage || "N/A",
        drawing_number: rfi.drawing_number || "N/A",
        cost_code_name: rfi.cost_code?.name || "N/A",
        schedule_impact_status:
          rfi.schedule_impact?.status === "yes_known"
            ? impactDisplayMap[rfi.schedule_impact?.status] + " - " + rfi.schedule_impact?.value
            : impactDisplayMap[rfi.schedule_impact?.status] || "N/A",
        cost_impact_status:
          rfi.cost_impact?.status === "yes_known"
            ? impactDisplayMap[rfi.cost_impact?.status] + " - " + rfi.cost_impact?.value
            : impactDisplayMap[rfi.cost_impact?.status] || "N/A",
        reference: rfi.reference || "N/A",
        private: rfi.private ? "Yes" : "No",
        status: rfi.status || "N/A",
      };

      return flattenedItem;
    });

    return mapped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const statusDisplayMap = {
    open: "Open",
    closed: "Closed",
    draft: "Draft",
  };

  const getStatusChip = (status = "N/A") => {
    const statusValue = status;
    const displayStatus = statusDisplayMap[statusValue] || statusValue;

    if (displayStatus === "Open") {
      return (
        <Chip
          label={displayStatus}
          size="small"
          sx={{
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            border: "1px solid #1976d2",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#1976d2",
            },
          }}
          icon={<LockOpenIcon fontSize="small" />}
        />
      );
    } else if (displayStatus === "Closed") {
      return (
        <Chip
          label={displayStatus}
          size="small"
          sx={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            border: "1px solid #2e7d32",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#2e7d32",
            },
          }}
          icon={<CheckCircleIcon fontSize="small" />}
        />
      );
    } else if (displayStatus === "Draft") {
      return (
        <Chip
          label={displayStatus}
          size="small"
          sx={{
            backgroundColor: "#f5f5f5",
            color: "#616161",
            border: "1px solid #616161",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#616161",
            },
          }}
          icon={<EditIcon fontSize="small" />}
        />
      );
    } else {
      return (
        <Chip
          label={displayStatus}
          size="small"
          color="default"
          sx={{
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
          }}
        />
      );
    }
  };

  const columns = [
    { field: "subject", headerName: "Subject" },
    { field: "number", headerName: "Number" },
    { field: "due_date", headerName: "Due Date (YYYY-MM-DD)" },
    { field: "rfi_manager_name", headerName: "RFI Manager" },
    { field: "assignee_name", headerName: "Assignee" },
    { field: "required_assignees", headerName: "Required Assignees" },
    { field: "received_from_name", headerName: "Received From" },
    { field: "distribution_list", headerName: "Distribution List" },
    { field: "responsible_contractor_name", headerName: "Responsible Contractor" },
    { field: "specification", headerName: "Specification" },
    { field: "location", headerName: "Location" },
    { field: "rfi_stage", headerName: "RFI Stage" },
    { field: "drawing_number", headerName: "Drawing Number" },
    { field: "cost_code_name", headerName: "Cost Code" },
    { field: "schedule_impact_status", headerName: "Schedule Impact (Days)" },
    { field: "cost_impact_status", headerName: "Cost Impact ($)" },
    { field: "reference", headerName: "Reference" },
    {
      field: "status",
      headerName: "Status",
      Cell: (value) => getStatusChip(value),
    },
    { field: "private", headerName: "Private" },
  ];

  return (
    <DataTable
      data={flattened}
      columns={columns}
      noDataMessage="No Existing RFIs Found"
      title={title}
    />
  );
};

RfiTable.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshTrigger: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
  title: PropTypes.string,
};

export default RfiTable;
